<script setup lang="ts">
import {
  getSingleSelectListItemTags,
  type SingleSelectListProps,
} from './single-select-list';
import ColorTag from '../ColorTag/ColorTag.vue';
import CollectionTag from '../CollectionTag/CollectionTag.vue';
import TTooltip from '../TTooltip.vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';

const COUNT_TAG_TOOLTIP_MAX_WIDTH = 280;

/** Neutral pills readable on primary-50 (selected) and slate-100 (hover) rows. */
const LIST_ITEM_TAG_CLASS =
  'inline-flex items-center rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/90';

const props = defineProps<SingleSelectListProps>();

const emits = defineEmits<{
  selectItem: [id: string];
}>();

const isNewGroup = (index: number): boolean => {
  const item = props.items[index];
  if (!item.groupLabel) {
    return false;
  }
  return props.items[index - 1]?.groupLabel !== item.groupLabel;
};
</script>

<template>
  <div class="flex flex-col items-center gap-2">
    <template v-for="(item, index) in props.items" :key="item.id">
      <p
        v-if="isNewGroup(index)"
        class="mt-2 w-full px-2 text-xs font-semibold tracking-[0.08em] text-slate-500 uppercase first:mt-0"
      >
        {{ item.groupLabel }}
      </p>
      <div
        class="flex w-full cursor-pointer items-stretch gap-2 rounded-md px-2 py-2 text-left transition-colors"
        :class="[
          item.id === props.selectedId
            ? 'bg-primary-50'
            : 'bg-transparent hover:bg-slate-100',
        ]"
        @click="emits('selectItem', item.id)"
      >
        <ColorTag
          v-if="item.color"
          fill-height
          :background-color="item.color"
        />
        <HugeIcon
          v-if="item.icon"
          class="mt-0.5 shrink-0 self-start text-slate-500"
          :size="16"
          :icon="item.icon"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <span class="text-sm text-slate-700">{{ item.label }}</span>
          <div
            v-if="getSingleSelectListItemTags(item).length"
            class="flex flex-wrap items-center gap-2"
          >
            <template
              v-for="tag in getSingleSelectListItemTags(item)"
              :key="`${item.id}-${tag.label}-${tag.variant}-${tag.tooltip ?? ''}`"
            >
              <CollectionTag
                v-if="tag.variant === 'collection'"
                :label="tag.label"
                show-icon
              />
              <TTooltip
                v-else-if="tag.tooltip"
                :content="tag.tooltip"
                :max-width="COUNT_TAG_TOOLTIP_MAX_WIDTH"
                placement="top"
                :interactive="false"
              >
                <span :class="LIST_ITEM_TAG_CLASS">{{ tag.label }}</span>
              </TTooltip>
              <span v-else :class="LIST_ITEM_TAG_CLASS">{{ tag.label }}</span>
            </template>
          </div>
        </div>
        <HugeIcon
          v-if="item.id === props.selectedId"
          class="text-primary-500 shrink-0 self-center"
          :size="16"
          icon="checkmark_circle"
        />
      </div>
    </template>
  </div>
</template>
